import { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import styled from "styled-components";
import { z } from "zod";
import { Time } from "../../../utils/time";
import { Button } from "../../atoms/Button";
import { Select } from "../../atoms/Select";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  selectableTime: Time[];
  handleSubmit: (hour: string, minute: string) => void;
  selectedTime?: Time;
};

export const PickUpTimeForm: React.FC<Props> = ({
  selectableTime,
  handleSubmit,
  selectedTime,
}) => {
  const [selectedHour, setSelectedHour] = useState<number | null>(
    selectedTime ? selectedTime.hour : null,
  );

  const schema = useMemo(() => {
    return z.object({
      hour: z.string().min(1),
      minute: z.string().min(1),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    any,
    undefined
  > | null>(null);

  const selectableTimeByHour = useMemo<Record<number, number[]>>(() => {
    const intervals: Record<number, number[]> = {};

    selectableTime.forEach((time) => {
      const { hour, minute } = time;

      if (intervals[hour]) {
        intervals[hour].push(minute);
      } else {
        intervals[hour] = [minute];
      }
    });

    return intervals;
  }, [selectableTime]);

  const selectableHours = useMemo(() => {
    return Object.keys(selectableTimeByHour).map((hour) => ({
      id: Number(hour),
      name: Number(hour) < 10 ? "0" + hour : hour,
      value: Number(hour) < 10 ? "0" + hour : hour,
    }));
  }, [selectableTimeByHour]);

  const selectableMinutes = useMemo(() => {
    return selectableTimeByHour[selectedHour ?? 0]?.map((minute) => ({
      id: Number(minute),
      name: minute === 0 ? "00" : minute.toString(),
      value: minute === 0 ? "00" : minute.toString(),
    }));
  }, [selectedHour, selectableTimeByHour]);

  const selectedMinutes = selectedTime
    ? selectedTime.minute < 10
      ? "0" + selectedTime.minute
      : selectedTime.minute.toString()
    : "";
  const isInclude = selectableMinutes?.find((value) => {
    return value.name === selectedMinutes;
  });
  const defaultMinutes = selectedTime && isInclude ? selectedMinutes : "";

  const defaultHour =
    defaultMinutes !== ""
      ? selectedTime
        ? selectedTime.hour < 10
          ? "0" + selectedTime.hour
          : selectedTime.hour.toString()
        : ""
      : "";

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        hour: defaultHour,
        minute: defaultMinutes,
      }}
      onSubmit={({ hour, minute }) => handleSubmit(hour, minute)}
    >
      <TimeSelectContainer>
        <Select
          name="hour"
          onChange={(name, value) => {
            setSelectedHour(Number(value));
            methods?.setValue("minute", "", { shouldValidate: true });
          }}
          options={selectableHours}
          placeholder="--"
          size="sm"
          defaultValue={defaultHour}
        />
        <p> : </p>
        <Select
          name="minute"
          onChange={() => {}}
          options={selectableMinutes}
          placeholder="--"
          disabled={selectedHour === null}
          size="sm"
          defaultValue={defaultMinutes}
        />
      </TimeSelectContainer>

      <TimeSelectButtonContainer>
        <Button
          btnStyle="primary"
          text="時間を指定してお受け取り"
          type="submit"
          size="full"
          rounded="md"
        />
      </TimeSelectButtonContainer>
    </FormTemplate>
  );
};

const TimeSelectContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 53px;
  p {
    line-height: 1em;
    font-size: 16px;
    font-weight: bold;
    padding: 0 20px;
    align-self: center;
  }
`;

const TimeSelectButtonContainer = styled.div`
  padding: 0 53px 28px;
  button {
    line-height: 1em;
    margin: 18px 0 0;
    padding: 20px 0 18px;
  }
`;
